import { createSignal, onCleanup, onMount, Suspense } from "solid-js";
import { Outlet, useRouterState } from "@tanstack/solid-router";

import { appState, useInteractionFlag } from "@fleet/ui";
import { AppBackground } from "@/components/app-background";
import { AppHeader } from "@/components/app-header";
import { OfflineBanner } from "@/components/offline-banner";
import { AppErrorBoundary } from "@/components/app-error-boundary";
import { useTheme } from "@/providers/theme-provider";

import { AppLayoutSidebar } from "./app-layout-sidebar";

/**
 * Slim mail-client app shell: brand background, sidebar (folders/nav), header,
 * offline banner and the routed <Outlet>. The template's todo/list shell
 * (list dialogs, drag-and-drop, smart-view counts, swipe navigation, todo
 * detail drawer and the PowerSync-backed command palette) was removed with the
 * todo domain — Cartero's primary surface is the mailbox.
 */
export function AppLayoutContent() {
  onMount(() => {
    // Tell the InitialSyncOverlay the shell is mounted and interactive.
    appState.markAsReady();
  });

  const theme = useTheme();
  const routerState = useRouterState();
  const isNavigating = () => routerState().status === "pending";

  const [isSidebarOpen, setIsSidebarOpen] = createSignal(false);

  // With the nav drawer open, the horizontal gesture belongs to it (drag to
  // dismiss). Cartero has no page-level swipe gesture yet, but the flag is the
  // declaration of who owns the horizontal gesture — the day one is added,
  // it stands down for free.
  useInteractionFlag({ "nav-drawer.open": isSidebarOpen });

  onMount(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isSidebarOpen()) {
        setIsSidebarOpen(false);
      }
    };
    globalThis.addEventListener("keydown", handleEscape);
    onCleanup(() => {
      globalThis.removeEventListener("keydown", handleEscape);
    });
  });

  return (
    <AppBackground
      class="flex h-dvh text-foreground overflow-hidden"
      isBlur={theme.isBlur()}
      patternType={theme.tokens()?.patternType}
      patternOpacity={theme.tokens()?.patternOpacity}
    >
      <AppLayoutSidebar isOpen={isSidebarOpen} setOpen={setIsSidebarOpen} />

      <main class="flex flex-1 flex-col overflow-hidden bg-transparent pt-[var(--safe-area-inset-top)] pr-[var(--safe-area-inset-right)] pb-[var(--safe-area-inset-bottom)]">
        <AppHeader onMenuClick={() => setIsSidebarOpen(true)} isNavigating={isNavigating} />
        <OfflineBanner />
        <div class="flex flex-1 flex-col min-h-0 overflow-hidden pr-[var(--safe-area-inset-right)] pl-[var(--safe-area-inset-left)]">
          <div class="flex flex-1 flex-col min-h-0 h-full">
            <AppErrorBoundary inline label="route">
              <div class="flex flex-1 flex-col min-h-0 h-full animate-slide-in-up will-change-[transform,opacity]">
                <Suspense fallback={null}>
                  <Outlet />
                </Suspense>
              </div>
            </AppErrorBoundary>
          </div>
        </div>
      </main>
    </AppBackground>
  );
}
